import { GraphDateType } from './dataKiGraph';
import {
  resolveGraphSlotChartHeight,
  resolveGraphSlotContainerHeight,
} from './graphSlotLayout';
import { GraphData } from './schemas';

/** 推移グラフ（機種比較）で同時表示できるグラフ項目の最大数 */
export const COMPARISON_GRAPH_MAX_FIELDS = 5;

/** 1本表示時のグラフ高さ（px）。既存の単一表示と同等 */
export const COMPARISON_GRAPH_CHART_HEIGHT = 360;

/** 2本表示時の1本あたりのグラフ高さ（px） */
export const COMPARISON_GRAPH_COMPACT_CHART_HEIGHT = 200;

/** 3本表示時の1本あたりのグラフ高さ（px） */
export const COMPARISON_GRAPH_DENSE_CHART_HEIGHT = 140;

/** 4本以上表示時の1本あたりのグラフ高さ（px） */
export const COMPARISON_GRAPH_OVERFLOW_CHART_HEIGHT = 120;

/**
 * 表示本数（スロット数）に応じたグラフ高さ（px）を返す。
 * 機種別推移と同じ標準値（360/200/140/120）を使う。
 */
export const resolveComparisonGraphChartHeight = (graphCount: number): number =>
  resolveGraphSlotChartHeight(graphCount, {
    single: COMPARISON_GRAPH_CHART_HEIGHT,
    double: COMPARISON_GRAPH_COMPACT_CHART_HEIGHT,
    triple: COMPARISON_GRAPH_DENSE_CHART_HEIGHT,
    overflow: COMPARISON_GRAPH_OVERFLOW_CHART_HEIGHT,
  });

/** タイトル・機種選択・枠線など、グラフ本体以外の高さ（px） */
const COMPARISON_GRAPH_CONTAINER_CHROME_HEIGHT = 48;
const COMPARISON_GRAPH_CONTAINER_TITLE_HEIGHT = 48;
const COMPARISON_GRAPH_CONTAINER_CONTROLS_HEIGHT = 104;
const COMPARISON_GRAPH_CONTAINER_SLOT_CONTROLS_HEIGHT = 56;
const COMPARISON_GRAPH_CONTAINER_SLOT_GAP = 16;
const COMPARISON_GRAPH_CONTAINER_FOOTER_HEIGHT = 52;

/**
 * 複数スロット表示時に ResizableWrapper が収めるべき高さ（px）を返す。
 * チェーン店レポート・店舗レポートの推移グラフ（機種比較）で共有する。
 */
export const resolveComparisonGraphContainerHeight = (
  slotCount: number
): number =>
  resolveGraphSlotContainerHeight(slotCount, {
    chromeHeight: COMPARISON_GRAPH_CONTAINER_CHROME_HEIGHT,
    titleHeight: COMPARISON_GRAPH_CONTAINER_TITLE_HEIGHT,
    controlsHeight: COMPARISON_GRAPH_CONTAINER_CONTROLS_HEIGHT,
    slotControlsHeight: COMPARISON_GRAPH_CONTAINER_SLOT_CONTROLS_HEIGHT,
    slotGap: COMPARISON_GRAPH_CONTAINER_SLOT_GAP,
    footerHeight: COMPARISON_GRAPH_CONTAINER_FOOTER_HEIGHT,
    resolveChartHeight: resolveComparisonGraphChartHeight,
  });

export type ComparisonGraphFieldItem = {
  field: string;
  data: GraphData;
};

export type ComparisonGraphParams = {
  /**
   * 項目コード
   */
  field?: string;
  /**
   * 画面に出すグラフ項目の一覧（最大 COMPARISON_GRAPH_MAX_FIELDS 件）。
   * お気に入り保存用。API リクエストでは使わず、Saga が1件ずつ field に分けて送る。
   */
  fields?: string[];
  /**
   * 機種コード一覧（未指定で全機種）
   */
  kiList?: string[];
  /**
   * 種別一覧（未指定で全種別）
   */
  shuList?: string[];
  /**
   * 種別グループID（未指定で全種別グループ）
   */
  shuGroupIds?: string[];
  /**
   * エリアID一覧（未指定で全エリア）
   */
  areas?: string[];
  /**
   * 対象店舗コード（未指定で全店舗）
   */
  halls?: string[];
  /**
   * 集計する日付範囲の開始日（デフォルト:当月の開始日）
   */
  startDate?: string;
  /**
   * 集計する日付範囲の最終日（デフォルト:当日）
   */
  endDate?: string;
  /**
   * 比較対象として集計する日付範囲の開始日（デフォルト:なし）
   */
  startComparisonDate?: string;
  /**
   * 比較対象として集計する日付範囲の最終日（デフォルト:なし）
   */
  endComparisonDate?: string;
  /**
   * 表示日付範囲に当日を除外するかどうか（デフォルト:true）
   */
  excludeToday?: boolean;
  /**
   * X軸として利用する日付タイプ
   */
  dateType?: GraphDateType;
};

export type ComparisonGraphResponse = {
  setting: ComparisonGraphParams;
  data: GraphData;
};

/**
 * Redux に保持する検索条件（期間・店舗・機種など）。
 * どのグラフ項目を表示するかは graphList[].field に持ち、setting には含めない。
 */
export type ComparisonGraphSetting = Omit<
  ComparisonGraphParams,
  'field' | 'fields'
>;

export type ComparisonGraph = {
  setting: ComparisonGraphSetting;
  graphList: ComparisonGraphFieldItem[];
  /**
   * お気に入り復元後・初回 fetch 前のグラフ項目（graphList が空のときのみ）
   */
  pendingFields?: string[];
};

/**
 * API レスポンスの setting から Redux 用 setting を作る。
 */
export const toComparisonGraphSetting = (
  params: ComparisonGraphParams
): ComparisonGraphSetting => {
  const { field: _field, fields: _fields, ...setting } = params;
  return setting;
};

export const buildComparisonGraphSyncId = (
  reportType: 'chainStoreKi' | 'hallKi',
  shuCode: string
): string => `${reportType}ComparisonGraph:${shuCode}`;
